import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { range } from 'lodash';
import moment from 'moment';
import type { MomentInput } from 'moment/moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Tooltip, ZIndex } from 'venn-ui-kit';
import {
  ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT,
  type AsOfDateSetting,
  CURRENT_AS_OF_DATE,
  LAST_UPLOADED_AS_OF_DATE,
  serializeAsOfDateSetting,
} from 'venn-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Match } from 'effect';

export type Frequency = 'MONTHLY' | 'QUARTERLY';

const getMonths = (year: number, begin: moment.Moment, ending: moment.Moment, frequency: Frequency) => {
  const months = frequency === 'MONTHLY' ? range(0, 12) : range(2, 12, 3);

  return months.filter((month) => {
    const monthEnd = moment.utc().year(year).month(month).endOf('month');
    return !monthEnd.isBefore(begin) && !monthEnd.isAfter(ending);
  });
};

const TopLevelItem = ({
  label,
  date,
  tooltip,
}: {
  label: React.ReactNode;
  date: MomentInput;
  tooltip: React.ReactNode;
}) => (
  <Tooltip content={tooltip} zIndex={ZIndex.Watermark} usePortal className="w-[210px]">
    <div className="flex justify-between w-[180px] items-center">
      {label}{' '}
      <span className="text-[12px] italic text-venn-grey-80">
        ({moment.utc(date).format(ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT)})
      </span>
    </div>
  </Tooltip>
);

type RootProps = React.ComponentProps<typeof Select> & {
  selectedDate: string;
  onValueChange: (value: string) => void;
  disabled: boolean;
  children: React.ReactNode;
};

type AccordionDateContentProps = {
  value: AsOfDateSetting;
  end: Date | undefined;
  frequency: Frequency;
  begin: moment.Moment;
  ending: moment.Moment;
  lastUpdatedValue: moment.Moment;
};

const StyledAccordionTrigger = styled(AccordionTrigger).attrs({
  className: 'text-[14px] text-[--foreground] p-[12px] h-[16px] hover:text-black',
})``;

const StyledSelectItem = styled(SelectItem).attrs({
  className:
    'hover:bg-venn-grey-20 align-middle text-[14px] indent-[24px] border-0 hover:border-0 focus:border-0 focus:outline-none',
})``;

type TriggerProps = {
  formattedDisplayDate: string;
  size: 'normal' | 'small';
};

const selectContentVariant = cva('', {
  variants: {
    variant: {
      accordion: 'overflow-hidden max-h-[200px]',
      thirtyDayCalendar: 'p-0',
    },
  },
});

const selectTriggerVariant = cva(
  'hover:text-black focus:ring-0 focus:outline-none focus:border-[hsl(var(--ring))] border-[hsl(var(--border))] border-[1px] border-solid text-[12px] font-bold text-[--foreground]',
  {
    variants: {
      size: {
        normal: 'h-[35px] w-[210px]',
        small: 'h-[20px] w-[100px] px-1 py-0',
      },
    },
  },
);

const selectValueVariant = cva('text-[12px] font-bold text-[--foreground] hover:text-black', {
  variants: {
    size: {
      normal: 'w-[210px]',
      small: 'w-[100px]',
    },
  },
});

export const CalendarDropdownPrimitives = {
  Root: ({ selectedDate, onValueChange, disabled, open, onOpenChange, children }: RootProps) => {
    return (
      <Select
        value={selectedDate}
        onValueChange={onValueChange}
        disabled={disabled}
        open={open}
        onOpenChange={onOpenChange}
      >
        {children}
      </Select>
    );
  },
  Trigger: ({ formattedDisplayDate: date, size }: TriggerProps) => (
    <SelectTrigger className={selectTriggerVariant({ size })}>
      <SelectValue placeholder="Select a date" defaultValue="Current" className={selectValueVariant({ size })}>
        {date}
      </SelectValue>
    </SelectTrigger>
  ),
  ContentRoot: ({ children, variant }: { children: React.ReactNode } & VariantProps<typeof selectContentVariant>) => (
    <SelectContent className={selectContentVariant({ variant })}>{children}</SelectContent>
  ),
  AccordionDateContent: ({ value, end, frequency, begin, ending, lastUpdatedValue }: AccordionDateContentProps) => {
    const [selectedYear, setSelectedYear] = useState<string | undefined>(
      Match.value(value).pipe(
        Match.tag('Current', () => undefined),
        Match.tag('LastUploaded', () => undefined),
        Match.tag('Date', ({ date }) =>
          String(moment.utc(date, ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT).endOf('month').year()),
        ),
        Match.exhaustive,
      ),
    );
    return (
      <>
        <SelectItem
          value={serializeAsOfDateSetting(CURRENT_AS_OF_DATE)}
          className={cn(
            'hover:bg-venn-grey-20 text-[14px] border-0 hover:border-0 focus:border-0 focus:outline-none w-[210px]',
          )}
        >
          <TopLevelItem
            label="Current"
            date={end}
            tooltip="Allocations reflect any drift due to investment performance since last uploaded"
          />
        </SelectItem>
        {lastUpdatedValue && (
          <SelectItem
            value={serializeAsOfDateSetting(LAST_UPLOADED_AS_OF_DATE)}
            className={cn(
              'hover:bg-venn-grey-20 text-[14px] border-0 hover:border-0 focus:border-0 focus:outline-none w-[210px]',
            )}
          >
            <TopLevelItem
              label="Last Uploaded"
              date={lastUpdatedValue}
              tooltip="Latest available uploaded allocations"
            />
          </SelectItem>
        )}
        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={selectedYear}
          onValueChange={(year) => setSelectedYear(year)}
        >
          {range(begin.year(), ending.year() + 1)
            .reverse()
            .filter((year) => getMonths(year, begin, ending, frequency).length > 0)
            .map((year) => (
              <AccordionItem
                value={String(year)}
                key={year}
                className="border-none hover:text-black hover:no-underline"
              >
                <StyledAccordionTrigger>
                  <span className="mx-[12px]">{year}</span>
                </StyledAccordionTrigger>
                {getMonths(year, begin, ending, frequency)
                  .reverse()
                  .map((month) => (
                    <AccordionContent key={`${year}-${month}`} className="h-[24px] border-none hover:border-none">
                      <StyledSelectItem
                        value={moment
                          .utc()
                          .year(year)
                          .month(month)
                          .endOf('month')
                          .format(ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT)}
                      >
                        {moment.utc().year(year).month(month).endOf('month').format('D MMM')}
                      </StyledSelectItem>
                    </AccordionContent>
                  ))}
              </AccordionItem>
            ))}
        </Accordion>
      </>
    );
  },
};
